import { useState, type ReactNode } from 'react';
import { FileSpreadsheet, FileText, Upload } from 'lucide-react';

import { ExportTablePrint, type PrintableTable } from '@/components/ExportTablePrint';
import { Button } from '@/components/ui/Button';
import { CheckboxGroup } from '@/components/ui/CheckboxGroup';
import { Modal } from '@/components/ui/Modal';
import { QueryError } from '@/components/ui/QueryError';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { cn } from '@/lib/utils';
import { downloadXlsx } from '@/lib/xlsx';
import type { ExportFieldOption, ExportRows } from '@/types/export';

type ExportFormat = 'xlsx' | 'pdf';

/**
 * Past this many columns a landscape A4 sheet stops being readable. Not a
 * limit — the office may still ask — just the point at which it is told.
 */
const PDF_COMFORTABLE_COLUMNS = 12;

export interface ExportDialogProps {
  /** Heads the dialog — "Download student list". */
  title: string;
  /** Heads the PDF and names the sheet — "Students 2026-27". */
  documentTitle: string;
  /** Without an extension: "students-2026-27". */
  fileName: string;
  /** "student" / "students", for what the toast says was saved. */
  noun: { one: string; other: string };
  /** Every detail this register can include. Asked for when the dialog opens. */
  loadFields: () => Promise<ExportFieldOption[]>;
  /**
   * The filter controls, owned by the caller so each register offers its own.
   * `onOpen` is where it copies the page's current filters into them.
   */
  filters: ReactNode;
  onOpen?: () => void;
  /** The filtered rows with the chosen details, from the server. */
  fetchRows: (fields: string[]) => Promise<ExportRows>;
  /** Remembers the ticked details per register, in this browser only. */
  storageKey: string;
  /** So the trigger matches the buttons beside it on each page. */
  triggerClassName?: string;
}

/**
 * The Export button on a register: choose who, choose which details, choose
 * Excel or PDF, then download.
 *
 * The rows come from an audited export endpoint; the file is written here —
 * Excel by `lib/xlsx.ts`, PDF by the browser's own "Save as PDF" through
 * `PrintDocument`, like every other paper this product produces.
 */
export function ExportDialog({
  title,
  documentTitle,
  fileName,
  noun,
  loadFields,
  filters,
  onOpen,
  fetchRows,
  storageKey,
  triggerClassName,
}: ExportDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  /** The table being printed; set only for the moment the print dialog is up. */
  const [printing, setPrinting] = useState<PrintableTable | null>(null);

  function open() {
    onOpen?.();
    setIsOpen(true);
  }

  return (
    <>
      <button
        type="button"
        onClick={open}
        className={cn('inline-flex items-center gap-2', triggerClassName)}
      >
        <Upload className="h-4 w-4" aria-hidden />
        <span>Export</span>
      </button>

      {isOpen && (
        <ExportDialogBody
          title={title}
          noun={noun}
          loadFields={loadFields}
          filters={filters}
          fetchRows={fetchRows}
          storageKey={storageKey}
          onClose={() => setIsOpen(false)}
          onExcel={(table) =>
            downloadXlsx(`${fileName}.xlsx`, documentTitle, [table.header, ...table.rows])
          }
          onPdf={(table) => setPrinting({ ...table, title: documentTitle, fileName })}
        />
      )}

      <ExportTablePrint table={printing} onFinished={() => setPrinting(null)} />
    </>
  );
}

interface ExportDialogBodyProps extends Pick<
  ExportDialogProps,
  'title' | 'noun' | 'loadFields' | 'filters' | 'fetchRows' | 'storageKey'
> {
  onClose: () => void;
  onExcel: (table: ExportRows) => void;
  onPdf: (table: ExportRows) => void;
}

/**
 * Mounted only while the dialog is open, so the field list is fetched when it
 * is wanted and the choices start fresh each time.
 */
function ExportDialogBody({
  title,
  noun,
  loadFields,
  filters,
  fetchRows,
  storageKey,
  onClose,
  onExcel,
  onPdf,
}: ExportDialogBodyProps) {
  const { data: fields, isLoading, error, refetch } = useApiQuery(loadFields, []);
  /** Null until the person touches a box: until then the remembered or default set applies. */
  const [chosen, setChosen] = useState<string[] | null>(null);
  const [format, setFormat] = useState<ExportFormat>('xlsx');
  const [isDownloading, setIsDownloading] = useState(false);

  const selected = chosen ?? (fields ? initialSelection(fields, storageKey) : []);
  const groups = fields ? groupFields(fields) : [];

  function choose(next: string[]) {
    setChosen(next);
  }

  async function download() {
    if (!fields || selected.length === 0) return;

    // In the catalogue's order, so the columns read the way the dialog lists them.
    const keys = fields.map((field) => field.key).filter((key) => selected.includes(key));
    rememberSelection(storageKey, keys);

    setIsDownloading(true);
    try {
      const table = await fetchRows(keys);
      if (table.rows.length === 0) {
        toast.info(
          'Nothing to download',
          'Nobody matches these filters. Widen them and try again.',
        );
        return;
      }

      const count = table.rows.length;
      const saved = `${count.toLocaleString('en-IN')} ${count === 1 ? noun.one : noun.other}`;
      // A file that is missing people must say so.
      const trimmed =
        table.skipped > 0
          ? `${table.skipped.toLocaleString('en-IN')} more could not fit in one file. Narrow the filters and download again.`
          : null;

      onClose();
      if (format === 'xlsx') {
        onExcel(table);
        toast.success(`${saved} saved`, trimmed ?? 'Check your downloads folder.');
      } else {
        onPdf(table);
        if (trimmed) toast.info(`Only ${saved} in this PDF`, trimmed);
      }
    } catch (caught) {
      toast.error(
        'Could not build the file',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsDownloading(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={title}
      description="Choose who to include and which details, then download."
      size="xl"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isDownloading}>
            Cancel
          </Button>
          <Button
            onClick={() => void download()}
            isLoading={isDownloading}
            disabled={!fields || selected.length === 0}
          >
            Download {format === 'xlsx' ? 'Excel' : 'PDF'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        <section aria-labelledby="export-filters" className="flex flex-col gap-3">
          <h3 id="export-filters" className="text-base font-semibold text-ink">
            Who to include
          </h3>
          {filters}
        </section>

        <section aria-labelledby="export-details" className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 id="export-details" className="text-base font-semibold text-ink">
              Details to include
              {fields && (
                <span className="ml-2 text-sm font-normal text-ink-3">
                  {selected.length} of {fields.length} selected
                </span>
              )}
            </h3>
            {fields && (
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => choose(fields.map((field) => field.key))}
                >
                  Select all
                </Button>
                <Button size="sm" variant="secondary" onClick={() => choose(defaultKeys(fields))}>
                  Default
                </Button>
                <Button size="sm" variant="secondary" onClick={() => choose([])}>
                  Clear
                </Button>
              </div>
            )}
          </div>

          {isLoading && <SkeletonLines count={6} />}
          {error && (
            <QueryError title="Could not load the details" error={error} onRetry={refetch} />
          )}
          {fields && fields.length === 0 && (
            <p className="text-sm text-ink-2">This list has no details to download yet.</p>
          )}

          {groups.map(([group, options]) => (
            <CheckboxGroup
              key={group}
              label={group}
              columns={3}
              options={options.map((field) => ({ value: field.key, label: field.label }))}
              // Only this group's ticks are passed in; the rest are kept as they were.
              value={selected.filter((key) => options.some((field) => field.key === key))}
              onChange={(groupValue) =>
                choose([
                  ...selected.filter((key) => !options.some((field) => field.key === key)),
                  ...groupValue,
                ])
              }
            />
          ))}
        </section>

        <section aria-labelledby="export-format" className="flex flex-col gap-3">
          <h3 id="export-format" className="text-base font-semibold text-ink">
            Format
          </h3>
          <div
            role="radiogroup"
            aria-labelledby="export-format"
            className="grid grid-cols-1 gap-2 sm:grid-cols-2"
          >
            <FormatOption
              value="xlsx"
              current={format}
              onChange={setFormat}
              icon={<FileSpreadsheet className="h-5 w-5" aria-hidden />}
              label="Excel (.xlsx)"
              hint="Every row, ready to sort, filter and add up."
            />
            <FormatOption
              value="pdf"
              current={format}
              onChange={setFormat}
              icon={<FileText className="h-5 w-5" aria-hidden />}
              label="PDF"
              hint='Choose "Save as PDF" in the print dialog.'
            />
          </div>
          {format === 'pdf' && selected.length > PDF_COMFORTABLE_COLUMNS && (
            <p className="text-xs text-ink-2">
              {selected.length} details is a lot for one printed page — the columns will be narrow.
              Excel suits a wide list better.
            </p>
          )}
        </section>
      </div>
    </Modal>
  );
}

function FormatOption({
  value,
  current,
  onChange,
  icon,
  label,
  hint,
}: {
  value: ExportFormat;
  current: ExportFormat;
  onChange: (value: ExportFormat) => void;
  icon: ReactNode;
  label: string;
  hint: string;
}) {
  const isChecked = value === current;
  return (
    <label
      className={cn(
        'flex cursor-pointer items-start gap-3 rounded-field border px-3 py-2.5 text-sm transition-colors',
        'focus-within:ring-2 focus-within:ring-accent-line',
        isChecked
          ? 'border-accent-line bg-accent-soft text-ink'
          : 'border-line bg-surface text-ink-2 hover:border-line-strong',
      )}
    >
      <input
        type="radio"
        name="export-format"
        value={value}
        checked={isChecked}
        onChange={() => onChange(value)}
        className="mt-0.5 h-4 w-4 shrink-0 accent-accent"
      />
      <span className="mt-0.5 shrink-0 text-ink-2">{icon}</span>
      <span className="min-w-0">
        <span className="block font-semibold text-ink">{label}</span>
        <span className="block text-xs text-ink-3">{hint}</span>
      </span>
    </label>
  );
}

/** The catalogue in its groups, in the order the server listed them. */
function groupFields(fields: ExportFieldOption[]): [string, ExportFieldOption[]][] {
  const groups = new Map<string, ExportFieldOption[]>();
  for (const field of fields) groups.set(field.group, [...(groups.get(field.group) ?? []), field]);
  return [...groups.entries()];
}

function defaultKeys(fields: ExportFieldOption[]): string[] {
  return fields.filter((field) => field.isDefault).map((field) => field.key);
}

/**
 * What this browser ticked last time, still on offer — else the defaults.
 *
 * Browser storage, not the account: it is a convenience, and an empty or
 * blocked store simply falls back to the defaults.
 */
function initialSelection(fields: ExportFieldOption[], storageKey: string): string[] {
  try {
    const stored: unknown = JSON.parse(localStorage.getItem(storageKey) ?? 'null');
    if (Array.isArray(stored)) {
      const known = new Set(fields.map((field) => field.key));
      const kept = stored.filter((key): key is string => typeof key === 'string' && known.has(key));
      if (kept.length > 0) return kept;
    }
  } catch {
    // Unreadable or blocked storage: the defaults below.
  }
  return defaultKeys(fields);
}

function rememberSelection(storageKey: string, keys: string[]): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify(keys));
  } catch {
    // Not remembered next time; nothing else depends on it.
  }
}
